import { Box, Divider, Paragraph, Text } from '@zillow/constellation-10';
import {
  AdornedInput,
  Adornment,
  IconClockOutline,
  Input,
  Popper,
  Tooltip,
  TooltipPopper,
} from '@zillow/constellation';
import { useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { isEqual } from '@zg-rentals/zdash';
import StringConstants from '../../constants/StringConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import dateTimeUtils from '../../utils/dateTime/dateTimeUtils';
import { StyledDiv, StyledDivVisibilitySensor, StyledPopover, StyledTimeValueContainer } from './StyledComponents';
import { IsVisible } from '@zg-rentals/react-use';

type Props = {
  onTimePickerChange(tourScheduledTimestampMs: string): void;
  tourScheduledTimestampMs: string | undefined | null;
};

type TimeValues = {
  hour: string | undefined;
  minute: string | undefined;
  meridiem: string | undefined;
};

const HOUR_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const MINUTE_OPTIONS = ['00', '15', '30', '45'];
const MERIDIEM_OPTIONS = ['am', 'pm'];
const TIME_PICKER_FORMAT = 'h:mm aaa';

const { getTimestampMsFromTimeValues, getTimeValuesFromTimestampMs } = dateTimeUtils;

const TimePicker = ({ onTimePickerChange, tourScheduledTimestampMs }: Props): JSX.Element => {
  const { isImpersonator } = useContext(AppContext);
  const [currentTimestampMs, setCurrentTimestampMs] = useState(tourScheduledTimestampMs);

  let formattedTimestampMs: string | undefined;
  if (currentTimestampMs) {
    formattedTimestampMs = format(+currentTimestampMs, TIME_PICKER_FORMAT);
  }

  const [timeValues, setTimeValues] = useState<TimeValues>(getTimeValuesFromTimestampMs(formattedTimestampMs));

  useEffect(() => {
    if (tourScheduledTimestampMs) {
      const formattedTimestampMs = format(+tourScheduledTimestampMs, TIME_PICKER_FORMAT);
      const formattedValueFromTimeStampMs = getTimeValuesFromTimestampMs(formattedTimestampMs);
      setTimeValues(formattedValueFromTimeStampMs);
    }
    setCurrentTimestampMs(tourScheduledTimestampMs);
  }, [tourScheduledTimestampMs]);

  const handleOnClick = (timeValueToUpdate: Partial<TimeValues>): void => {
    const newTimeValues = {
      ...timeValues,
      ...timeValueToUpdate,
    };

    // Nothing to update
    if (isEqual(newTimeValues, timeValues)) {
      return;
    }

    const newTimestampMs = getTimestampMsFromTimeValues({
      newTimeValues,
      currentTimestampMs,
    });

    setTimeValues(newTimeValues);
    setCurrentTimestampMs(newTimestampMs);
    onTimePickerChange(newTimestampMs);
  };

  if (!tourScheduledTimestampMs || isImpersonator) {
    return <TooltipContent isImpersonator={isImpersonator} />;
  }

  return (
    <Popper
      triggered={
        <StyledPopover
          body={
            <>
              <Box css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
                <Box css={{ display: 'flex', flexDirection: 'row' }}>
                  <StyledDiv>
                    <Paragraph textStyle="body-bold" css={{ textAlign: 'center' }}>
                      Hr
                    </Paragraph>
                  </StyledDiv>

                  <StyledDiv>
                    <Paragraph textStyle="body-bold" css={{ textAlign: 'center' }}>
                      Min
                    </Paragraph>
                  </StyledDiv>

                  <StyledDiv>
                    <span>{/* placeholder for alignment */}</span>
                  </StyledDiv>
                </Box>
              </Box>

              <Divider />

              <Box css={{ paddingTop: 'default', paddingBottom: 'default' }}>
                <Box css={{ display: 'flex', flexDirection: 'row' }}>
                  <StyledDiv isScrollable={true}>
                    {HOUR_OPTIONS.map((hour) => {
                      let className = '';

                      if (timeValues.hour && timeValues.hour === hour) {
                        className = 'TimePicker-highlighted-value';
                      }

                      return (
                        <StyledTimeValueContainer
                          className={className}
                          key={hour}
                          onClick={() => {
                            handleOnClick({ hour });
                          }}
                        >
                          <Text>{hour}</Text>
                        </StyledTimeValueContainer>
                      );
                    })}

                    <IsVisible options={{ rootMargin: '0px 0px -80px 0px' }}>
                      {({ ref }) => <StyledDivVisibilitySensor ref={ref} />}
                    </IsVisible>
                  </StyledDiv>

                  <StyledDiv>
                    {MINUTE_OPTIONS.map((minute) => {
                      let className = '';

                      if (timeValues.minute && timeValues.minute === minute) {
                        className = 'TimePicker-highlighted-value';
                      }

                      return (
                        <StyledTimeValueContainer
                          className={className}
                          key={minute}
                          onClick={() => {
                            handleOnClick({ minute });
                          }}
                        >
                          <Text>{`:${minute}`}</Text>
                        </StyledTimeValueContainer>
                      );
                    })}
                  </StyledDiv>

                  <StyledDiv>
                    {MERIDIEM_OPTIONS.map((meridiem) => {
                      let className = '';

                      if (timeValues.meridiem && timeValues.meridiem === meridiem) {
                        className = 'TimePicker-highlighted-value';
                      }

                      return (
                        <StyledTimeValueContainer
                          className={className}
                          key={meridiem}
                          onClick={() => {
                            handleOnClick({ meridiem });
                          }}
                        >
                          <Text>{meridiem}</Text>
                        </StyledTimeValueContainer>
                      );
                    })}
                  </StyledDiv>
                </Box>
              </Box>
            </>
          }
        ></StyledPopover>
      }
    >
      <AdornedInput
        input={<Input value={formattedTimestampMs} onChange={handleOnClick} />}
        leftAdornment={AdornedIcon}
      />
    </Popper>
  );
};

const AdornedIcon = (
  <Adornment>
    <IconClockOutline />
  </Adornment>
);

const TooltipContent = ({ isImpersonator }: { isImpersonator: boolean }): JSX.Element => {
  const tooltipText = isImpersonator ? StringConstants.TOOLTIP.IMPERSONATION : 'Please provide a date first.';
  const tooltip = <Tooltip body={<Paragraph>{tooltipText}</Paragraph>} />;

  return (
    <TooltipPopper triggered={tooltip}>
      <AdornedInput disabled={true} input={<Input placeholder="00:00 am" />} leftAdornment={AdornedIcon} />
    </TooltipPopper>
  );
};

export default TimePicker;
